import React, { PropsWithChildren } from "react";
import { enhancedContentSourceLocation } from "../../lib/ContentSource";
import { hasCategoryAndEventName, TabEvent } from "../../lib/Events";
import { DbRecordedEvent } from "../../State/EvidenceDB/DatabaseTypes";
import { describeEncodedSearchTermUsingFunctionNotationHtml } from "../../lib/Search/EncodedSearchTerm";
import { isDbNetworkEventWithSearchResults } from "../../ServiceWorker/NetworkEventDbSearchWebWorker";
import { InstrumentationEventDomEventFired } from "../../lib/Events/InstrumentationEvent";
import { FilteredSearchResultsState } from "./FilteredSearchResultsState";
import { Observer, observer } from "mobx-react-lite";
import { styled } from "styled-components";
import { RecordingSearchState } from "./RecordingSearchState";
import { getRegisteredDomain } from "../../lib/utils/domains";
import { ContextLine, ContextSpan, EmphasizedResultSpan, Literal, TimeStampElement } from "./common-styles";
import { InteractiveRequestId } from "./CommonElements";
import { RecordingViewState } from "./RecordingViewState";
import { DbNetworkEventWithSearchResults } from "../../lib/searchRecordedEvent";
import { SearchResultEvent } from "../../lib/DbEventSearcher";

const isDomEventFiredEvent = hasCategoryAndEventName('Instrumentation','DomEventFired');

const SearchResultContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	min-width: 0;
	margin-left: 1rem;
	margin-top: 2px;
	margin-bottom: 2px;
	overflow-x: hidden;
	text-overflow: ellipsis;
`;

const SearchResultFindingsContainer = styled.div`
	background-color: rgba(0,0,255,0.05);
	padding: 2px;
`;
const SearchResultContextContainer = styled.div`
	margin-left: 1rem;
	font-size: 80%;
`

const MetaEventElement = styled(SearchResultContainer)`
	display: block;
	border-left: 0.25rem solid rgba(189, 103, 16, 0.25);
	background-color: rgba(189, 103, 16, 0.075);
	padding-left: 0.25rem;
`;

const SearchTermFoundElement = styled(Literal)`
	font-weight: bold;
`;

const DestinationFoundElement = styled(Literal)`
	font-weight: bold;
`;


const QuotedLiteral = (props: PropsWithChildren<{}>) => {
	return <>&ldquo;<Literal>{props.children}</Literal>&rdquo;</>;
}





export const MsSinceRecordingStartView = ({msSinceRecordingStart}: {msSinceRecordingStart: number}) => {
	const ms = Math.floor(msSinceRecordingStart % 1000);
	const seconds = Math.floor(msSinceRecordingStart / 1000) % 60;
	const minutes = Math.floor(msSinceRecordingStart / 60000) % 60;
	const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
	return <TimeStampElement>{timeStr}</TimeStampElement>;
}

export const SearchResultEventResultView = ({
	searchResultEvent, msSinceRecordingStart, recordingViewState
}: {
	recordingViewState: RecordingViewState;
	searchResultEvent: DbNetworkEventWithSearchResults;
	msSinceRecordingStart: number;}
) => {
	return (<>{
		searchResultEvent.searchResults.map( (searchResult, index) => {
			const key = `domain-detail-${searchResultEvent.eventIndex}:${searchResult.encodedSearchTerm}:${searchResultEvent.raw.requestId}:${searchResultEvent.category}:${searchResultEvent.eventName}:${enhancedContentSourceLocation(searchResult)}:${searchResult.position}`
			return (
				<SearchResultContainer key={key} >
					<SearchResultFindingsContainer>
						<MsSinceRecordingStartView msSinceRecordingStart={msSinceRecordingStart} />
						<SearchTermFoundElement>{ searchResult.originalSearchTerm }</SearchTermFoundElement>
						&#32;{ searchResult.direction === 'sent' ? 'sent to' : 'received from' }&nbsp;
						<DestinationFoundElement>{ getRegisteredDomain(searchResultEvent.destination.url) }</DestinationFoundElement>
						&#32;
						{searchResult.encodingSteps.length === 0 ? null : (<>
							encoded as&#32;
							<Literal>{ describeEncodedSearchTermUsingFunctionNotationHtml(searchResult.encodingSteps, searchResult.originalSearchTerm)}</Literal>&#32;
						</>
						)}
						{
							("location" in searchResult && typeof searchResult.location === "string") ? (<>
									at&#32;
									<Literal>{enhancedContentSourceLocation(searchResult)}:{searchResult.position}</Literal>
									&#32;
								</>) : null
						}
						of request&#32;
						<InteractiveRequestId displayJson={recordingViewState.setObjectToDisplayAsJson} event={searchResultEvent} />
					</SearchResultFindingsContainer>
					{ searchResult.prefix.length === 0 && searchResult.suffix.length === 0 ? null : (
						<ContextLine>
							<ContextSpan>{ searchResult.prefix }</ContextSpan>
							<EmphasizedResultSpan>{ searchResult.stringFound }</EmphasizedResultSpan>
							<ContextSpan>{ searchResult.suffix }</ContextSpan>
						</ContextLine>
				)}
				</SearchResultContainer>
			)
		})}
	</>);
}


const MetaEvent = ({children, msSinceRecordingStart}: PropsWithChildren<{msSinceRecordingStart: number}>) => (
	<MetaEventElement>
		<MsSinceRecordingStartView msSinceRecordingStart={msSinceRecordingStart} />
		{children}
	</MetaEventElement>
)

export const DomEventFiredEventView = ({domEventFiredEvent, msSinceRecordingStart}: {
	domEventFiredEvent: DbRecordedEvent<InstrumentationEventDomEventFired>;
	msSinceRecordingStart: number;
}) => {
	const elementIdentifier = domEventFiredEvent.raw.elementId ?? domEventFiredEvent.raw.elementName ?? "";
	const forElementHtml = elementIdentifier.length === 0 ? null : (<> for <Literal>elementIdentifier</Literal></>);

	switch (domEventFiredEvent.raw.domOrCustomEventName) {
		case "input":
		case "change":
			if (domEventFiredEvent.raw.value != null) {
				return <MetaEvent {...{msSinceRecordingStart}}>User set input value{forElementHtml} to: <QuotedLiteral>{domEventFiredEvent.raw.value}</QuotedLiteral></MetaEvent>;
			}
			break;
		case "submit":
			return <MetaEvent {...{msSinceRecordingStart}}>Form submitted{forElementHtml}</MetaEvent>;
		case "click":
			return (
			<MetaEvent {...{msSinceRecordingStart}}>User clicked on {domEventFiredEvent.raw.elementType} element{
					elementIdentifier.length === 0 ? "" : (<> with id <Literal>elementIdentifier</Literal></>)
				}</MetaEvent>);
		case "focusin":
			return (
				<MetaEvent {...{msSinceRecordingStart}}>User focused on {domEventFiredEvent.raw.elementType} element{
					elementIdentifier.length === 0 ? ""  : (<>with id <Literal>elementIdentifier</Literal></>)
				}</MetaEvent>);
	}
}

export const TabEventView = ({tabEvent, msSinceRecordingStart}: {tabEvent: DbRecordedEvent<TabEvent>, msSinceRecordingStart: number;}) => {
	switch (tabEvent.eventName) {
		case 'activated':
			return <MetaEvent {...{msSinceRecordingStart}}>User (re-)activated tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'removed':
			return <MetaEvent {...{msSinceRecordingStart}}>User removed tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'startRecording':
			return <MetaEvent {...{msSinceRecordingStart}}>User started recording tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'stopRecording':
			return <MetaEvent {...{msSinceRecordingStart}}>User stopped recording tab <Literal>{tabEvent.tab.tabId}</Literal> ({tabEvent.tab.url})</MetaEvent>;
		case 'updated': {
			const { url } = tabEvent.raw;
			if (url != null) {
				return <MetaEvent {...{msSinceRecordingStart}}>Loaded page <Literal>{url}</Literal> in tab <Literal>{tabEvent.tab.tabId}</Literal></MetaEvent>;
			}
		}
			break;
		case 'created': {
			const { id, url } = tabEvent.raw;
			if (url != null) {
				return (<MetaEvent {...{msSinceRecordingStart}}>User created new tab <Literal>{id}</Literal> with initial URL <Literal>{url}</Literal></MetaEvent>);
			}
		}
			break;
		default:
			break;
	}
}

export const FilteredSearchResultsEventView = ({
	recordingViewState,
	searchResultEvent, msSinceRecordingStart
}: {
	recordingViewState: RecordingViewState;
	searchResultEvent: SearchResultEvent;
	msSinceRecordingStart: number;
}) => {
	if (isDbNetworkEventWithSearchResults(searchResultEvent)) {
		return <SearchResultEventResultView {...{recordingViewState, searchResultEvent, msSinceRecordingStart}} />;
	} else if (isDomEventFiredEvent(searchResultEvent)) {
		return <DomEventFiredEventView domEventFiredEvent={searchResultEvent} {...{msSinceRecordingStart}} />;
	} else if (searchResultEvent.category === "Tabs") {
		return <TabEventView tabEvent={searchResultEvent} {...{msSinceRecordingStart}} />;
	}
}

const SearchResultsContainer = styled.div`
	min-width: 0;
`;

/**
 * 
 * 
 * add a search term filter
 * 
 * 
 */
export const FilteredSearchResultsView = observer( ({recordingSearchState}: {recordingSearchState: RecordingSearchState}) => {
	const filteredSearchResultsState = new FilteredSearchResultsState(recordingSearchState);
	return (
		<Observer>{ () => (<>
			<SearchResultsContainer>
				{filteredSearchResultsState.filteredSearchResults.map( (searchResultEvent) => {
					return <FilteredSearchResultsEventView
						recordingViewState={recordingSearchState.recordingViewState}
						key={`${searchResultEvent.eventIndex}`}
						searchResultEvent={searchResultEvent}
						msSinceRecordingStart={new Date(searchResultEvent.whenRecordedIso).getTime() - recordingSearchState.dbRecordingEntryWriter.startTimeMs} />;
				})}
			</SearchResultsContainer>
		</>)}
		</Observer>
	);
})